import * as React from 'react'

/* TYPES */

export interface VisibilityConfig {
  initialIsVisible?: boolean
}

interface VisibilityApi {
  hide: () => any
  isVisible: boolean
  show: () => any
  toggle: () => any
}

interface Props extends VisibilityConfig {
  children?: React.ReactNode
}

export interface RenderProps extends VisibilityConfig {
  children: (props: VisibilityApi) => React.ReactElement<{}>
}

/* HOOK */

export const useVisibility = ({
  initialIsVisible = false,
}: VisibilityConfig): VisibilityApi => {
  const [isVisible, setVisibility] = React.useState(initialIsVisible)

  const hide = () => setVisibility(false)

  const show = () => setVisibility(true)

  const toggle = isVisible ? hide : show

  return { isVisible, hide, show, toggle }
}

/* DECORATOR */

export const withVisibility = <T extends {}>(config: VisibilityConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { visibility: VisibilityConfig }) => (
  <Component
    {...props}
    visibility={useVisibility({ ...config, ...props.visibility })}
  />
)

/* RENDER PROP */

export const Visibility = ({ children, ...props }: RenderProps) => {
  const visibility = useVisibility(props)

  return typeof children === 'function' ? children(visibility) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<VisibilityProvider>\``
  )

export const VisibilityContext = React.createContext<VisibilityApi>({
  isVisible: false,
  hide: noop('hide'),
  show: noop('show'),
  toggle: noop('toggle'),
})

export const VisibilityProvider = ({ children, ...props }: Props) => (
  <VisibilityContext.Provider value={useVisibility(props)}>
    {children}
  </VisibilityContext.Provider>
)

export const consumeVisibility = () => React.useContext(VisibilityContext)
